import { ProductSelector } from '@/components/flows/FlowEditor/properties/ProductSelector';
import { Field } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { type ShopifyProduct } from '@/hooks/queries/use-shopify-products-query';
import { Box, HStack, Image, Input, Text, VStack } from '@chakra-ui/react';
import { type MentionBoostSetupProductDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

interface SelectedBoostProductRowProps {
  product: MentionBoostSetupProductDto;
  subline: string;
  // When true the subline is a prose status (not a URL), so it is not monospaced.
  sublineIsStatus: boolean;
}

// Compact selected-product row, mirroring the gifting node's SelectedProductItem:
// 28px thumbnail · title · secondary line carrying the resolved destination URL.
function SelectedBoostProductRow({
  product,
  subline,
  sublineIsStatus,
}: SelectedBoostProductRowProps) {
  return (
    <HStack gap={2} px={3} py={2.5} align="center">
      {product.imageUrl ? (
        <Image
          src={product.imageUrl}
          alt={product.title}
          boxSize="28px"
          objectFit="cover"
          borderRadius="sm"
          flexShrink={0}
        />
      ) : (
        <Box boxSize="28px" bg="bg.muted" borderRadius="sm" flexShrink={0} />
      )}
      <VStack flex={1} minW={0} gap={0} align="stretch">
        <Text fontSize="xs" fontWeight="medium" truncate>
          {product.title}
        </Text>
        <Text
          fontSize="2xs"
          color="fg.muted"
          fontFamily={sublineIsStatus ? undefined : 'mono'}
          truncate
        >
          {subline}
        </Text>
      </VStack>
    </HStack>
  );
}

interface ProductDestinationProps {
  customTargetUrlEnabled: boolean;
  onCustomTargetUrlChange: (event: { checked: boolean | 'indeterminate' }) => void;
  onProductToggle: (product: ShopifyProduct) => void;
  onTargetUrlChange: (url: string) => void;
  selectedProduct: MentionBoostSetupProductDto | null;
  selectedProductIds: string[];
  targetUrl: string;
  targetUrlIsInvalid: boolean;
  targetUrlPreview: string | null;
}

export function ProductDestination({
  customTargetUrlEnabled,
  onCustomTargetUrlChange,
  onProductToggle,
  onTargetUrlChange,
  selectedProduct,
  selectedProductIds,
  targetUrl,
  targetUrlIsInvalid,
  targetUrlPreview,
}: ProductDestinationProps) {
  const { t } = useTranslation();

  // While a custom URL is active, the product page is no longer the target, so
  // the row's secondary line states that rather than echoing the product URL.
  const subline = customTargetUrlEnabled
    ? t('mentions.boost.setup.customTargetUrlActiveHint')
    : (targetUrlPreview ?? t('mentions.boost.setup.destinationUrlUnavailable'));

  return (
    <>
      <ProductSelector
        onToggle={onProductToggle}
        selectedProductIds={selectedProductIds}
        singleSelect
      />

      {selectedProduct && (
        <Box border="1px solid" borderColor="border.muted" borderRadius="md" overflow="hidden">
          <SelectedBoostProductRow
            product={selectedProduct}
            subline={subline}
            sublineIsStatus={customTargetUrlEnabled}
          />

          <Box borderTop="1px solid" borderColor="border.muted" px={3} py={2.5}>
            <HStack justify="space-between" gap={3} align="flex-start">
              <Box minW={0}>
                <Text fontSize="xs" fontWeight="medium">
                  {t('mentions.boost.setup.customTargetUrlLabel')}
                </Text>
                <Text fontSize="2xs" color="fg.muted" mt={0.5}>
                  {t('mentions.boost.setup.customTargetUrlDescription')}
                </Text>
              </Box>
              <Switch
                checked={customTargetUrlEnabled}
                onCheckedChange={onCustomTargetUrlChange}
                colorPalette="primary"
                size="sm"
                flexShrink={0}
                inputProps={{
                  'aria-label': t('mentions.boost.setup.customTargetUrlLabel'),
                }}
              />
            </HStack>

            {customTargetUrlEnabled && (
              <Box mt={2}>
                <Field
                  invalid={targetUrlIsInvalid}
                  errorText={t('mentions.boost.setup.customTargetUrlError')}
                >
                  <Input
                    size="sm"
                    variant="outline"
                    type="url"
                    value={targetUrl}
                    placeholder={t('mentions.boost.setup.customTargetUrlPlaceholder')}
                    onChange={(event) => onTargetUrlChange(event.target.value)}
                  />
                </Field>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </>
  );
}
